import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { events, track } from 'src/configs/amplitude';
import { useStore } from 'src/stores';
import { YEmailValidateDefault } from 'src/utils/formValidation/yupValidators';
import useStyles from './styles';
export const ResidentForm = observer(_ref => {
  let validator = _ref.validator,
    onEmailValidationChange = _ref.onEmailValidationChange;
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showEmailWarning = _useState2[0],
    setShowEmailWarning = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isValidatingEmail = _useState4[0],
    setIsValidatingEmail = _useState4[1];
  const handleChange = store.enrollResident.changeInUnitValue;
  const handleEmailBlur = async e => {
    const email = e.target.value;
    if (!email) return;
    validator.handleBlur(e);
    track(events.filled('Resident Email'));
    if (!YEmailValidateDefault.isValidSync(email)) return;
    try {
      var _resp$response;
      setIsValidatingEmail(true);
      const resp = await store.account.validateEmailAsync.run({
        email
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful && ((_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.result) !== 'deliverable') {
        onEmailValidationChange(true);
        setShowEmailWarning(true);
      } else {
        onEmailValidationChange(false);
      }
    } catch (_unused) {
      onEmailValidationChange(false);
    } finally {
      setIsValidatingEmail(false);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('general.personal_details')), /*#__PURE__*/React.createElement("div", {
    className: classes.smSection
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    rowSpacing: 24,
    columnSpacing: 10
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    error: validator.touched.firstName && Boolean(validator.errors.firstName),
    fullWidth: true,
    helperText: validator.touched.firstName && validator.errors.firstName,
    label: t('general.first_name'),
    id: "firstName",
    name: "firstName",
    onBlur: e => {
      if (e.target.value) {
        validator.handleBlur(e);
        track(events.filled('Resident First Name'));
      }
    },
    onChange: e => {
      validator.handleChange(e);
      handleChange('firstName', e.target.value);
    },
    variant: "outlined",
    value: validator.values.firstName
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    id: "lastName",
    name: "lastName",
    label: t('general.last_name'),
    variant: "outlined",
    value: validator.values.lastName,
    onBlur: e => {
      if (e.target.value) {
        validator.handleBlur(e);
        track(events.filled('Resident Last Name'));
      }
    },
    onChange: e => {
      validator.handleChange(e);
      handleChange('lastName', e.target.value);
    },
    error: validator.touched.lastName && Boolean(validator.errors.lastName),
    helperText: validator.touched.lastName && validator.errors.lastName
  })), localStorage.getItem('livable_demo_mode') !== 'Demo' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    error: validator.touched.primaryEmail && Boolean(validator.errors.primaryEmail),
    fullWidth: true,
    helperText: validator.touched.primaryEmail && validator.errors.primaryEmail,
    label: t('general.email_address_optional'),
    InputProps: {
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "end",
        type: "infoTooltip"
      }, isValidatingEmail ? /*#__PURE__*/React.createElement(CircularProgress, {
        size: 20
      }) : /*#__PURE__*/React.createElement(ExpandedTooltip, {
        type: "grey",
        title: t('enrollment_modal.email_tooltip'),
        placement: "top"
      }, /*#__PURE__*/React.createElement(IconButton, null, /*#__PURE__*/React.createElement(Icon, {
        type: "question"
      }))))
    },
    id: "primaryEmail",
    name: "primaryEmail",
    onBlur: handleEmailBlur,
    onChange: e => {
      validator.handleChange(e);
      onEmailValidationChange(false);
      if (YEmailValidateDefault.isValidSync(e.target.value) && store.enrollResident.enrollResidentInUnit.automaticallySendPaperInvitations) {
        store.enrollResident.changeInUnitValue('automaticallySendPaperInvitations', false);
        store.enrollResident.changeInUnitValue('automaticallySendPaperStatements', false);
      }
      handleChange('primaryEmail', e.target.value);
    },
    type: "email",
    value: validator.values.primaryEmail,
    variant: "outlined"
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(InputMask, {
    mask: "999-999-9999",
    onBlur: e => {
      if (!validator.touched.phone && /\d/.test(e.target.value)) validator.setFieldTouched('phone', true);
      track(events.filled('Resident Phone'));
    },
    onChange: e => {
      validator.handleChange(e);
      handleChange('phone', e.target.value);
    },
    value: validator.values.phone
  }, inputProps => /*#__PURE__*/React.createElement(TextField, _extends({}, inputProps, {
    error: validator.touched.phone && Boolean(validator.errors.phone),
    fullWidth: true,
    helperText: validator.touched.phone && validator.errors.phone,
    label: t('general.personal_phone_optional'),
    id: "phone",
    name: "phone",
    type: "tel",
    variant: "outlined"
  }))))))), /*#__PURE__*/React.createElement(Dialog, {
    open: showEmailWarning,
    onClose: () => setShowEmailWarning(false)
  }, /*#__PURE__*/React.createElement(DialogTitle, null, "Email Validation"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(DialogContentText, null, "The email address you entered appears to be invalid or undeliverable. Please verify the email address and try again.")), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setShowEmailWarning(false),
    variant: "contained"
  }, "OK"))));
});